// Card Teaser Component
// Front face of the swipe card showing basic information

import React, { useRef, useEffect } from 'react';
import { CardTeaserProps } from '../../types/swipe';
import {
  CardTeaserContainer,
  CardPhoto,
  CardVideo,
  CardTeaserInfo,
  CardName,
  CardAge,
  CardLocation,
  CardHint,
  CardDistance,
  CardInfoRow
} from '../../styles/Swipe/Swipe.styles';
import { logger } from '../../utils/logger';

export const CardTeaser: React.FC<CardTeaserProps> = ({
  teaser,
  isActive = true,
  shouldPreload = false
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  
  /**
   * Only the card on top plays.
   *
   * The stack renders three cards and every one of them called play() on
   * mount, so a phone was decoding three videos at once while also trying to
   * animate a drag at 60fps. Two of those three are behind the top card and
   * cannot be seen.
   */
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !teaser.video) return;

    if (isActive) {
      // From the top. A card that has been sitting in the stack may have a
      // buffered position already, and picking up mid-clip is the other half
      // of the "skip" — the viewer sees a frame from the middle before the
      // opening plays.
      if (video.currentTime > 0) video.currentTime = 0;
      video.play().catch(err => {
        logger.log('Video autoplay prevented:', err);
      });
    } else {
      video.pause();
    }
  }, [teaser.video, isActive]);
  
  const formatDistance = (distance?: number): string => {
    if (!distance) return '';
    if (distance < 1) return '< 1 mile away';
    if (distance === 1) return '1 mile away';
    return `${Math.round(distance)} miles away`;
  };

  return (
    <CardTeaserContainer>
      {teaser.video ? (
        <CardVideo
          ref={videoRef}
          src={teaser.video}
          loop
          muted
          playsInline
          // The top card and the one behind it are fully buffered; anything
          // deeper fetches only enough for its poster.
          //
          // The next card has to be ready BEFORE it is promoted. Buffering it
          // on promotion is what produced the loading flash: the poster held
          // while the first frames arrived, every single time a card was
          // swiped away.
          preload={isActive || shouldPreload ? 'auto' : 'metadata'}
          poster={teaser.videoPoster || teaser.photo}
        />
      ) : (
        <CardPhoto $photoUrl={teaser.photo} />
      )}
      
      {teaser.distance && (
        <CardDistance>
          {typeof teaser.distance === 'number' ? formatDistance(teaser.distance) : teaser.distance}
        </CardDistance>
      )}
      
      <CardTeaserInfo>
        <CardName>
          {teaser.name}
        </CardName>
        
        <CardInfoRow>
          <CardAge>
            {teaser.age}
          </CardAge>
          <CardLocation>
            {teaser.location}
          </CardLocation>
        </CardInfoRow>
        
        <CardHint>
          {teaser.hint}
        </CardHint>
      </CardTeaserInfo>
    </CardTeaserContainer>
  );
};
